/**
 * Agent chat service for communicating with the ADK agent service via SSE.
 *
 * Uses the same workspace header pattern as api.service.ts:
 * reads workspace ID from shared module state (getWorkspaceId())
 * rather than requiring it as a parameter on every call.
 */

import { v4 as uuidv4 } from 'uuid';
import type { ApplySpreadsheetOpsResult, SpreadsheetChange } from '@bower/shared';
import { AGENT_SERVICE_URL } from '../config/env';
import { authService } from './auth.service';
import { msSinceMemoryChange } from './memory.service';
import { getWorkspaceId } from '../utils/workspaceState';
import { copyTextToClipboard } from '../utils/clipboard';

/** A context entity as the /api/chat multipart field expects it. */
export interface WireContextItem {
  readonly type: string;
  readonly id: string;
  readonly name: string;
  /** Set only for a native spreadsheet: the open tab, and only when valid. */
  readonly sheet?: string;
  /** The on-screen A1 selection, and only when it matches the server's grammar. */
  readonly selection?: string;
}

export interface GroundingSource {
  title: string;
  url: string;
  domain: string;
  index: number;
}

export type ActivityKind = 'tool_call' | 'tool_result';

/**
 * A tool call ADK has PAUSED pending the researcher's approval.
 *
 * Nothing has run at this point and nothing will until a reply carrying `id`
 * goes back — ADK then resumes and invokes the tool itself. The model is never
 * asked to re-issue the call, which is what makes this loop-proof: the old
 * flow depended on the model noticing a bare "yes" and remembering to call
 * again, and in production it did not.
 *
 * `args` are the pending call's own arguments, verbatim. For merge/delete that
 * is entity ids and a tool name, which the UI resolves to titles from data it
 * already holds — but it is NOT UGC-free in general:
 * `create_visual_reference_set` carries a draft whose category names the
 * researcher wrote. Treat this payload as workspace content. It is for the
 * authenticated researcher's own browser and nowhere else: never log it, and
 * never put it in an analytics property bag or an error-tracking breadcrumb
 * (`rules/egress-policy.md`). The backend says the same at
 * `chat_tool_confirmation.build_confirmation_payloads`; keep the two in step.
 */
export interface PendingToolConfirmation {
  /** Echoed back verbatim; ADK pairs the reply to the paused call by this. */
  readonly id: string;
  readonly tool: string;
  readonly args: Readonly<Record<string, unknown>>;
  /** ADK's own generic sentence — a fallback when the UI has no better copy. */
  readonly hint: string;
  /**
   * Set CLIENT-SIDE when this prompt was restored by a history replay rather
   * than delivered live on the turn that raised it. Never sent by the server —
   * one payload serves both routes in, so only the client knows which it took.
   * Analytics-only; nothing about answering the call depends on it.
   */
  readonly replayed?: boolean;
}

/** One selectable option in a paused question. */
export interface ChoiceOption {
  /**
   * The `const` the MODEL authored, returned to it verbatim. Opaque here on
   * purpose: it is usually an entity UUID, and the model never has to re-derive
   * which option was picked from a display string. Never render it.
   */
  readonly value: string;
  readonly label: string;
  /** What distinguishes this option from a same-labelled sibling. May be ''. */
  readonly detail: string;
}

/**
 * A question ADK has paused the turn on (`adk_request_input`).
 *
 * The sibling of `PendingToolConfirmation`, and it carries MORE workspace
 * content than one: `message`, and every option's `label` and `detail`, are
 * entity titles the researcher wrote and the model selected for display. Same
 * handling rule and it matters more here — the authenticated researcher's own
 * browser and nowhere else. Never log it, and never put it in an analytics
 * property bag or an error-tracking breadcrumb (`rules/egress-policy.md`). The
 * backend says the same at `chat_tool_input.build_choice_payloads`; keep the
 * two in step.
 *
 * `options` may be EMPTY. The model authors the response schema and is free to
 * write one that is not a list of choices (free text, a boolean). The client
 * then renders the question as prose and the researcher answers in the
 * composer, which is what they would have done before this channel existed.
 */
export interface PendingToolChoice {
  /** Echoed back verbatim; ADK pairs the answer to the paused call by this. */
  readonly id: string;
  readonly message: string;
  readonly options: ReadonlyArray<ChoiceOption>;
  /** Set client-side on a history replay. See `PendingToolConfirmation`. */
  readonly replayed?: boolean;
}

/**
 * The chat change card for one `apply_spreadsheet_ops` call: the tool result's
 * card fields, picked server-side by `services/spreadsheet_changes.py`. Built
 * from the tool result, never from model text (P2-D4). `summary`, `detail` and
 * `problem` can name a sheet or a range, which is the researcher's own content:
 * render it, never log it or put it in an analytics property.
 */
export type SpreadsheetChangeCardData = Pick<
  ApplySpreadsheetOpsResult,
  'artifactId' | 'status' | 'summary' | 'changes' | 'undoToken'
> & {
  /**
   * Set ONLY by this client, from the latest `spreadsheet_undo` frame for this
   * card's token: `undone`, or the refusal's code. The agent undid (or tried
   * to undo) the change from chat, so the card shows what its own Undo would
   * have. Never persisted: after a reload the card looks as it did before.
   */
  undoOutcome?: string;
};

export interface SSEEvent {
  type:
    | 'text'
    | 'agent_transfer'
    | 'sources'
    | 'done'
    | 'error'
    | 'activity'
    | 'confirmation'
    | 'choice'
    | 'spreadsheet_changes'
    | 'spreadsheet_undo';
  data: {
    /** One complete assistant-event payload, never a token delta. */
    content?: string;
    author?: string;
    agent?: string;
    status?: string;
    message?: string;
    /**
     * Set ONLY by this client, and only on an `error` it raises BEFORE the
     * stream opens — the turn never reached the agent service, so no transcript
     * will ever carry it back.
     *
     * Absent means delivered, which is the safe default precisely because a
     * stream error is parsed off the wire and the server never sets this. If
     * absence meant undelivered, every errored turn would be held back as
     * unacknowledged and duplicate itself against the next snapshot.
     */
    undelivered?: boolean;
    /**
     * Machine code on an `error` frame, beside the human `message`. The quota
     * refusal carries one, and so does `turn_in_progress`: the session is
     * still running an earlier turn and the server turned this one away
     * (`TURN_IN_PROGRESS_CODE` in `agentChatTurnEvents.ts`).
     */
    code?: string;
    sources?: GroundingSource[];
    trace_id?: string;
    // Activity event fields (tool_call / tool_result). Tool args and
    // response bodies are never sent — only id, kind, tool, label, ok.
    id?: string;
    kind?: ActivityKind;
    tool?: string;
    label?: string;
    // Choice event fields. `message` is shared with the error event above —
    // the two never arrive on one frame, and splitting the union by type would
    // cost more than it buys here.
    options?: ChoiceOption[];
    ok?: boolean;
    // Confirmation event: the paused call's arguments plus ADK's hint. `id`
    // and `tool` above are reused.
    args?: Readonly<Record<string, unknown>>;
    hint?: string;
    // Spreadsheet change card (`spreadsheet_changes`). `status` above is
    // reused and carries 'applied' | 'failed' on this frame.
    artifactId?: string;
    summary?: string;
    changes?: SpreadsheetChange[];
    undoToken?: string;
    // Agent undo (`spreadsheet_undo`): `artifactId` and `undoToken` above name
    // the card; this is `undone` or the refusal's machine code.
    outcome?: string;
  };
}

export type SSEEventHandler = (event: SSEEvent) => void;

/**
 * Build common headers for agent service requests.
 * Mirrors the pattern in api.service.ts interceptors.
 */
async function buildHeaders(): Promise<Record<string, string> | null> {
  const token = await authService.getAuthToken();
  if (!token) return null;

  const workspaceId = getWorkspaceId();

  const headers: Record<string, string> = {
    Authorization: `Bearer ${token}`,
    'x-correlation-id': uuidv4(),
  };

  if (workspaceId) {
    headers['x-workspace-id'] = workspaceId;
  }

  // A memory edit, delete or save in Settings: the agent refetches its cached
  // memory bundle instead of serving the old fact for one more turn. Sent as an
  // age, not a timestamp, so skew between this clock and the server's is moot.
  const msAgo = msSinceMemoryChange();
  if (msAgo !== null) {
    headers['x-memory-changed-ms-ago'] = String(Math.round(msAgo));
  }

  return headers;
}

export interface SendAgentMessageToolPicks {
  /** Per-turn connector toggles from the composer picker. Empty array
   *  means "no gateway tools this turn"; absent means "the user hasn't
   *  connected anything". Both are forwarded verbatim to the agent. */
  readonly enabledConnectorToolkits?: ReadonlyArray<string>;
  /** Web-search composer toggle. Absent → server default (currently on). */
  readonly webSearchEnabled?: boolean;
  /**
   * Answering a paused, confirmation-gated tool call rather than sending a new
   * instruction. When present the server resumes that call and IGNORES the
   * message text — deliberately, so an approval can never be re-read by the
   * model as a fresh instruction.
   */
  readonly toolConfirmation?: { readonly callId: string; readonly confirmed: boolean };
  /**
   * Answering a paused question rather than sending a new instruction. Same
   * contract as `toolConfirmation`: the server resumes that call and IGNORES
   * the message text, so a pick can never be re-read as a fresh instruction.
   * `value` is the chosen option's opaque `value`, never its label.
   */
  readonly toolChoice?: { readonly callId: string; readonly value: string };
}

/** One composer file already saved by the resumable upload. Only the id and
 * the reserve's bounded intake code travel: the agent reads name, type and
 * size from the artifact itself, so the client cannot assert them. */
export interface ChatAttachmentRef {
  readonly id: string;
  readonly intakeNote?: string;
}

/** A turn's composer files sent as artifact ids instead of bytes. */
export interface ChatUploadedAttachments {
  readonly refs: readonly ChatAttachmentRef[];
  /** Files whose upload failed; the agent words them as today's FAILED line. */
  readonly failedCount: number;
}

/**
 * Send a message to the agent and stream the response via SSE.
 * Returns an AbortController to allow cancellation.
 *
 * `uploaded` is the turn's composer files already saved by the resumable
 * upload (a writer's turn): it is sent as `attachments` ids and `files` is
 * then not sent. Absent, the request is the multipart one.
 * `turnController` is the controller the id path's uploads ran on, so one
 * cancel covers the uploads and the POST; absent, one is made here as before.
 */
export async function sendAgentMessage(
  message: string,
  sessionId: string,
  onEvent: SSEEventHandler,
  context?: readonly WireContextItem[],
  files?: File[],
  toolPicks?: SendAgentMessageToolPicks,
  uploaded?: ChatUploadedAttachments,
  turnController?: AbortController,
): Promise<AbortController> {
  const headers = await buildHeaders();
  if (!headers) {
    // Nothing left the browser, so the turn is the researcher's alone to keep.
    onEvent({ type: 'error', data: { message: 'Not authenticated', undelivered: true } });
    return new AbortController();
  }

  const controller = turnController ?? new AbortController();

  // Always send as FormData to match the multipart endpoint
  const formData = new FormData();
  formData.append('message', message);
  formData.append('session_id', sessionId);
  formData.append('context', JSON.stringify(context || []));
  formData.append('timezone', Intl.DateTimeFormat().resolvedOptions().timeZone);
  if (toolPicks?.enabledConnectorToolkits) {
    formData.append('enabled_connectors', JSON.stringify(toolPicks.enabledConnectorToolkits));
  }
  if (toolPicks?.toolConfirmation) {
    formData.append('tool_confirmation', JSON.stringify(toolPicks.toolConfirmation));
  }
  if (toolPicks?.toolChoice) {
    formData.append('tool_choice', JSON.stringify(toolPicks.toolChoice));
  }
  if (typeof toolPicks?.webSearchEnabled === 'boolean') {
    formData.append('web_search_enabled', String(toolPicks.webSearchEnabled));
  }
  if (uploaded) {
    // The ids replace the bytes outright: the agent 422s a turn carrying both.
    // Each ref is rebuilt so only the two keys its strict model accepts leave.
    const refs = uploaded.refs.map(({ id, intakeNote }) =>
      intakeNote ? { id, intakeNote } : { id },
    );
    formData.append('attachments', JSON.stringify(refs));
    if (uploaded.failedCount > 0) {
      formData.append('attachments_failed', String(uploaded.failedCount));
    }
  } else if (files) {
    for (const file of files) {
      formData.append('files', file);
    }
  }
  // This client renders approval cards (ToolConfirmationPrompt). Without it the
  // agent refuses a link after a Model Armor flag instead of pausing for one.
  formData.append('approvals_supported', 'true');

  let response: Response;
  try {
    response = await fetch(`${AGENT_SERVICE_URL}/api/chat`, {
      method: 'POST',
      headers,
      body: formData,
      signal: controller.signal,
    });
  } catch (error) {
    // Only a caller's controller can abort this early (the id path's, which
    // is already the turn's abortRef): the turn was canceled or left before
    // the POST answered, and the cancel marker is the record of it.
    if (controller.signal.aborted) return controller;
    throw error;
  }

  if (!response.ok) {
    // The route answered non-2xx, so it did not stream a turn. A 5xx COULD in
    // principle have persisted something before failing, which would make this
    // row duplicate against a later snapshot — accepted deliberately, because
    // the other way round deletes a message the researcher wrote, and a visible
    // duplicate is recoverable where a silent deletion is not (rule 12).
    onEvent({
      type: 'error',
      data: { message: `Request failed: ${response.status}`, undelivered: true },
    });
    return controller;
  }

  const reader = response.body?.getReader();
  if (!reader) {
    // NOT `undelivered`: the response was 2xx, so the service accepted this
    // turn and its transcript will carry it. Only the body is missing.
    onEvent({ type: 'error', data: { message: 'No response body' } });
    return controller;
  }

  const decoder = new TextDecoder();
  let buffer = '';

  const processStream = async (): Promise<void> => {
    try {
      let streamDone = false;
      while (!streamDone) {
        const { done, value } = await reader.read();
        if (done) {
          streamDone = true;

          // Process any remaining buffered event
          if (buffer.trim()) {
            parseSSEBlock(buffer, onEvent);
          }
          continue;
        }

        buffer += decoder.decode(value, { stream: true });

        // SSE events are separated by double newlines
        const blocks = buffer.split('\n\n');
        // Keep the last incomplete block in the buffer
        buffer = blocks.pop() || '';

        for (const block of blocks) {
          parseSSEBlock(block, onEvent);
        }
      }
    } catch (err) {
      if (err instanceof DOMException && err.name === 'AbortError') {
        return;
      }
      onEvent({ type: 'error', data: { message: 'Stream interrupted' } });
    }
  };

  void processStream();

  return controller;
}

/**
 * Parse a single SSE block (event + data lines) and emit it.
 */
function parseSSEBlock(block: string, onEvent: SSEEventHandler): void {
  const lines = block.split('\n');
  let eventType: string | null = null;
  const dataLines: string[] = [];

  for (const line of lines) {
    if (line.startsWith('event:')) {
      eventType = line.slice(6).trim();
    } else if (line.startsWith('data:')) {
      dataLines.push(line.slice(5).trimStart());
    }
  }

  if (eventType && dataLines.length > 0) {
    try {
      const data = JSON.parse(dataLines.join('\n'));
      onEvent({
        type: eventType as SSEEvent['type'],
        data,
      });
    } catch {
      // Skip malformed JSON
    }
  }
}

export interface AgentSessionSummary {
  session_id: string;
  user_id: string;
  last_update_time: number;
  title?: string | null;
}

export interface AgentSessionPage {
  sessions: AgentSessionSummary[];
  /** Total matching conversations, so the caller knows if more exist. */
  total: number;
  /**
   * The page could not be fetched. Callers must not write `total` from a failed
   * response: doing so set it to 0, which reads as "nothing more to load" and
   * silently removed the only way to reach older conversations.
   */
  failed?: boolean;
}

/** Page size for the conversation list. Matches the palette's page size. */
export const SESSION_PAGE_SIZE = 20;

/**
 * List sessions for the authenticated user, one page at a time.
 *
 * The endpoint used to return every conversation unbounded and the chat panel
 * loaded all of them into a menu. Callers now ask for a page and use `total`
 * to decide whether to fetch more.
 */
export async function listAgentSessionPage(
  offset = 0,
  limit = SESSION_PAGE_SIZE,
): Promise<AgentSessionPage> {
  const headers = await buildHeaders();
  if (!headers) return { sessions: [], total: 0, failed: true };

  const params = new URLSearchParams({ limit: String(limit), offset: String(offset) });
  const response = await fetch(`${AGENT_SERVICE_URL}/api/sessions?${params}`, {
    headers,
  });

  if (!response.ok) return { sessions: [], total: 0, failed: true };

  const data = await response.json();
  return {
    sessions: data.sessions ?? [],
    // Older deployments of the agent service don't send `total`. Falling back
    // to the page length means "no more pages", which degrades to the previous
    // single-page behaviour rather than looping forever on a missing field.
    total: typeof data.total === 'number' ? data.total : (data.sessions?.length ?? 0),
  };
}

/** One matching conversation, anchored at its best-matching message. */
export interface ChatSearchResult {
  session_id: string;
  /** The message to scroll to once the conversation opens. */
  message_id: string;
  title?: string | null;
  role: string;
  /**
   * Snippet with matched terms wrapped in the delimiters below. Rendered by
   * splitting on them into text nodes — never as markup, because this is user
   * and model content.
   */
  snippet: string;
  timestamp?: number | null;
}

/**
 * Highlight delimiters used by the server's `ts_headline` call.
 *
 * Unicode private-use code points, chosen so they cannot occur in real prose
 * and carry no meaning in any renderer. These MUST match
 * `HIGHLIGHT_START` / `HIGHLIGHT_END` in the agent service's
 * `session_search_query.py` — a documented sibling pair across the two
 * languages, like the Cloud Tasks max-attempts constant on the backend.
 */
export const SNIPPET_HIGHLIGHT_START = '\ue000';
export const SNIPPET_HIGHLIGHT_END = '\ue001';

export interface ChatSearchPage {
  results: ChatSearchResult[];
  total: number;
  /**
   * The search could not complete — it hit the server's statement ceiling, or
   * the request failed. NOT the same as "no matches", and the UI must not
   * render it as such: telling someone their own history lacks a word they know
   * they used is a confident wrong answer, and there is nothing to retry
   * because it looks like a successful empty result.
   */
  degraded: boolean;
}

/**
 * Full-text search across the user's own chat history in this workspace.
 *
 * Returns an empty page on any failure rather than throwing: search is a
 * secondary surface inside the command palette, and an outage here should
 * quietly show no chat results rather than break the whole palette.
 */
export async function searchChatHistory(
  query: string,
  offset = 0,
  limit = 20,
): Promise<ChatSearchPage> {
  const trimmed = query.trim();
  if (!trimmed) return { results: [], total: 0, degraded: false };

  const headers = await buildHeaders();
  if (!headers) return { results: [], total: 0, degraded: false };

  try {
    const params = new URLSearchParams({
      q: trimmed,
      limit: String(limit),
      offset: String(offset),
    });
    const response = await fetch(`${AGENT_SERVICE_URL}/api/sessions/search?${params}`, {
      headers,
    });

    // A failed request is a DEGRADED search, not an empty one.
    if (!response.ok) return { results: [], total: 0, degraded: true };

    const data = await response.json();
    return {
      results: data.results ?? [],
      total: data.total ?? 0,
      // Older deployments don't send the field; absent means the search ran.
      degraded: data.degraded === true,
    };
  } catch {
    return { results: [], total: 0, degraded: true };
  }
}

export interface HistoryAttachment {
  name: string;
}

export interface HistoryMessage {
  role: string;
  content: string;
  /**
   * The ADK event id this message came from. Optional because the field is
   * additive on the wire — but it is what a chat-search result points at, so
   * scroll-to-message needs it present.
   */
  message_id?: string;
  author?: string;
  timestamp?: number;
  sources?: GroundingSource[];
  attachments?: HistoryAttachment[];
  /** The turn's spreadsheet change cards, rebuilt from the persisted tool result. */
  spreadsheet_changes?: SpreadsheetChangeCardData[];
}

export interface SessionHistoryResult {
  messages: HistoryMessage[];
  /**
   * Calls ADK is still holding paused in this session, so the Confirm/Cancel
   * control comes back after a reload. A confirmation is not a message — it is
   * a function call with no text — so it cannot ride in `messages`, and
   * without it a researcher who reloaded mid-approval had no way to answer and
   * the call stayed paused forever.
   *
   * Always an array. An older server omits the field, which reads the same as
   * "nothing is paused" — the safe direction, and the pre-fix behaviour.
   */
  pendingConfirmations: PendingToolConfirmation[];
  pendingChoices: PendingToolChoice[];
  /**
   * A turn is still running on this session, on some other request: another
   * tab, another device, the mobile app's orphaned stream. The server runs one turn per
   * session at a time. `null` when it could not be found out (the server's
   * probe failed or was slow, or this read failed): the hook keeps its previous
   * answer then. False on an older server that omits the field.
   */
  turnInProgress: boolean | null;
  /** null = success, 'not_found' = no session yet, 'error' = server/network failure */
  error: null | 'not_found' | 'forbidden' | 'error';
}

/** The wire's tri-state: true held, false free, null unknown; absent (an older server) is false. */
function wireTurnInProgress(value: unknown): boolean | null {
  if (value === null) return null;
  return value === true;
}

/**
 * Get message history for a session from the backend.
 * Returns messages reconstructed from ADK session events,
 * with structured error info so callers can differentiate
 * "no session" from actual failures.
 */
export async function getSessionHistory(sessionId: string): Promise<SessionHistoryResult> {
  const headers = await buildHeaders();
  if (!headers)
    return {
      messages: [],
      pendingConfirmations: [],
      pendingChoices: [],
      turnInProgress: null,
      error: 'error',
    };

  try {
    const response = await fetch(`${AGENT_SERVICE_URL}/api/sessions/${sessionId}/history`, {
      headers,
    });

    if (response.status === 404)
      return {
        messages: [],
        pendingConfirmations: [],
        pendingChoices: [],
        turnInProgress: false,
        error: 'not_found',
      };
    if (response.status === 403)
      return {
        messages: [],
        pendingConfirmations: [],
        pendingChoices: [],
        turnInProgress: false,
        error: 'forbidden',
      };
    if (!response.ok)
      return {
        messages: [],
        pendingConfirmations: [],
        pendingChoices: [],
        turnInProgress: null,
        error: 'error',
      };

    const data = await response.json();
    return {
      messages: data.messages,
      pendingConfirmations: data.pending_confirmations ?? [],
      pendingChoices: data.pending_choices ?? [],
      turnInProgress: wireTurnInProgress(data.turn_in_progress),
      error: null,
    };
  } catch {
    return {
      messages: [],
      pendingConfirmations: [],
      pendingChoices: [],
      turnInProgress: null,
      error: 'error',
    };
  }
}

/**
 * Stop the session's running turn on the server: the Stop button.
 *
 * Aborting the stream does not do it. Deployed, neither the edge nor Cloud Run
 * passes the abort through, so the turn ran to its end and kept the session
 * locked (measured 2026-10-14; `services/chat_turn_lock.py` in the agent).
 * Resolves false when the Stop was not delivered: the turn then runs on, and
 * the history read shows it still working.
 */
export async function stopAgentTurn(sessionId: string): Promise<boolean> {
  const headers = await buildHeaders();
  if (!headers) return false;

  try {
    const response = await fetch(`${AGENT_SERVICE_URL}/api/sessions/${sessionId}/stop`, {
      method: 'POST',
      headers,
    });
    return response.ok;
  } catch {
    return false;
  }
}

/**
 * Rename a session title.
 */
export async function renameAgentSession(sessionId: string, title: string): Promise<boolean> {
  const headers = await buildHeaders();
  if (!headers) return false;

  const response = await fetch(`${AGENT_SERVICE_URL}/api/sessions/${sessionId}/title`, {
    method: 'PATCH',
    headers: { ...headers, 'Content-Type': 'application/json' },
    body: JSON.stringify({ title }),
  });

  return response.ok;
}

/**
 * Copy session conversation as plain text to clipboard.
 * Uses getSessionHistory() which already enforces workspace isolation.
 */
export async function copySessionAsText(sessionId: string): Promise<boolean> {
  const result = await getSessionHistory(sessionId);
  if (result.error) return false;

  // A carrier row (a spreadsheet change card with no answer to ride on) has no
  // text, so it contributes no "[Bower] " line of its own. Its cards still
  // do: they may be the only record of writes still in the workbook.
  const text = result.messages
    .flatMap((m) => {
      const label = m.role === 'user' ? 'User' : 'Bower';
      const lines = m.content.trim() !== '' ? [`[${label}] ${m.content}`] : [];
      for (const card of m.spreadsheet_changes ?? []) {
        lines.push(`[Bower] Spreadsheet: ${card.summary}`);
      }
      return lines;
    })
    .join('\n\n');

  // NOTE: unlike every other copy in the app, this one has already awaited a
  // network round trip, so it may land outside the click's transient user
  // activation — on which the execCommand fallback depends. It is best-effort
  // here; a caller that needs it reliable has to hold the text before the
  // click. The async API, when permitted, has no such constraint.
  return copyTextToClipboard(text);
}

/**
 * Delete a session.
 */
export async function deleteAgentSession(sessionId: string): Promise<boolean> {
  const headers = await buildHeaders();
  if (!headers) return false;

  const response = await fetch(`${AGENT_SERVICE_URL}/api/sessions/${sessionId}`, {
    method: 'DELETE',
    headers,
  });

  return response.ok;
}

/**
 * How long to wait before the single retry below. Sized to clear the trace's
 * export + ingestion window: the agent service's span exporter batches on a
 * ~5s schedule and Langfuse ingests asynchronously after that.
 */
const FEEDBACK_RETRY_DELAY_MS = 10_000;

async function postChatFeedback(
  traceId: string,
  score: number,
  comment?: string,
): Promise<boolean> {
  const headers = await buildHeaders();
  if (!headers) return false;

  try {
    const response = await fetch(`${AGENT_SERVICE_URL}/api/feedback`, {
      method: 'POST',
      headers: { ...headers, 'Content-Type': 'application/json' },
      body: JSON.stringify({ trace_id: traceId, score, comment }),
    });
    return response.ok;
  } catch {
    return false;
  }
}

/**
 * Submit chat feedback (thumbs up/down) to the agent service.
 * Score: 1 = positive, 0 = negative.
 *
 * Retries once, after a delay, because the agent service verifies that the
 * trace belongs to the caller's workspace before scoring it — and it verifies
 * against Langfuse, which has not necessarily ingested the trace yet when a
 * user rates an answer the moment they finish reading it. The first attempt
 * lands inside that window fairly often; the retry lands after it. Without
 * this, a legitimate rating is silently dropped and the thumb springs back.
 *
 * Deliberately retries on ANY failure rather than on a distinguishing status:
 * the service answers 404 for "not yours" and "not ingested yet" alike, so
 * that a caller cannot use the response to discover whether another
 * workspace's trace exists. Retrying a genuine denial simply fails twice.
 */
export async function submitChatFeedback(
  traceId: string,
  score: number,
  comment?: string,
): Promise<boolean> {
  if (await postChatFeedback(traceId, score, comment)) return true;

  await new Promise((resolve) => setTimeout(resolve, FEEDBACK_RETRY_DELAY_MS));
  return postChatFeedback(traceId, score, comment);
}
